// @flow
import type {
  BlokComponentPropsType,
  BlokComponentType,
  CardImageType,
  BaseCardsType
} from 'components/types';
import renderComponent from 'components/editable/renderComponent';
import { interpolate } from 'react-spring/web.cjs';
import Template from '../Template';
import Carousel from '../Carousel';
import { CardImage, ErrorCard, CardImageGrid } from './static';
import {
  AnimatedImageCard,
  AnimatedErrorCard,
  CARD_DISPLAYS
} from './carousel';
import * as componentKeys from '../../../../componentKeys';

const fillerCardComponentKey = 'error-card';
const fillerCards = [
  {
    component: fillerCardComponentKey,
    _uid: 'error-1',
    _editable: 'false'
  },
  {
    component: fillerCardComponentKey,
    _uid: 'error-2',
    _editable: 'false'
  },
  {
    component: fillerCardComponentKey,
    _uid: 'error-3',
    _editable: 'false'
  }
];

const componentMap = {
  [componentKeys.CARD_IMAGE]: CardImage,
  [fillerCardComponentKey]: ({ key }: { key: string }) => (
    <ErrorCard key={key}>A minimum of 3 cards is required</ErrorCard>
  )
};

const animatedComponentMap = {
  [componentKeys.CARD_IMAGE]: CardImage,
  [fillerCardComponentKey]: ({
    key,
    style
  }: {
    key: string,
    style: Object
  }) => (
    <AnimatedErrorCard key={key} style={style}>
      At least 6 cards are required to use a carousel
    </AnimatedErrorCard>
  )
};

const renderComponentCustomPath =
  'components/editable/Cards/CardImage/index.jsx';
function springsRenderer(
  springs: *[],
  cards: BlokComponentType<CardImageType>[]
): any {
  return springs.map((styles, index) =>
    renderComponent(
      cards[index],
      {
        key: cards[index]._uid + index,
        customPath: renderComponentCustomPath,
        style: {
          ...styles,
          transform: interpolate(
            [styles.translateX, styles.opacity],
            (translateXVal) => `translate(${translateXVal}px, -50%)`
          )
        },
        component: AnimatedImageCard,
        onDragStart: (event) => {
          // This prevents the default "drag" effect of links in Safari
          event.preventDefault();
          return false;
        }
      },
      animatedComponentMap
    )
  );
}

const ImageCards = ({
  blok
}: BlokComponentPropsType<{
  ...BaseCardsType,
  cards: BlokComponentType<CardImageType>[]
}>) => {
  const {
    title,
    sub_title: subTitle,
    description,
    cards: originalCards,
    call_to_action: callToAction,
    call_to_action_text: callToActionText
  } = blok;
  const useCarousel = originalCards.length > 5;
  let cards = originalCards;
  if (cards.length < 3) {
    cards = [].concat(originalCards, fillerCards).slice(0, 3);
  }
  let children;
  if (useCarousel) {
    children = (
      <Carousel
        cards={cards}
        title={title}
        cardDisplays={CARD_DISPLAYS}
        springsRenderer={springsRenderer}
      />
    );
  } else {
    children = (
      <CardImageGrid totalCards={cards.length}>
        {cards.map((card, index) =>
          renderComponent(
            card,
            {
              customPath: renderComponentCustomPath,
              key: card._uid + index,
              totalCards: cards.length
            },
            componentMap
          )
        )}
      </CardImageGrid>
    );
  }
  return (
    <Template
      callToAction={callToAction}
      callToActionText={callToActionText}
      description={description}
      subTitle={subTitle}
      title={title}
      useCarousel={useCarousel}
    >
      {children}
    </Template>
  );
};

export default ImageCards;
